<template>
  <CreativesApprovalCore
    ref="creativesApprovalCore"
    :title="title"
    :creatives="creatives"
    :items-total-count="creativesTotalCount"
    :allow-selection="allowSelection"
    :is-from-deal-details-view="isFromDealDetailsView"
    :columns="columns"
    :sort-column="sortColumn"
    :fixed-filters="creativeFixedFilters"
    :table-id="creativesTableId"
    :is-loading="isLoading"
    :show-approve-button="showApproveButton"
    :show-reject-button="showRejectButton"
    :show-approve-for-specific-inventory-button="showApproveForSpecificInventoryButton"
    :selected-items-count="selectedCreatives?.length"
    :text-search-properties="[`creative.name`, `creative.advertiser.name`]"
    track-by="mediaOwnerApprovalID"
    @selection-changed="selectedCreatives = $event"
    @fetch-items="(queryParams) => fetchCreatives(queryParams)"
    @approve-selected-items="approveSelectedInternalCreatives"
    @reject-selected-items="rejectSelectedInternalCreatives"
    @approve-for-specific-inventory="approveInternalCreativesForSpecificInventory">
    <template #approveForSpecificNetworksButton>
      <button
        v-if="ENABLE_APPROVE_FOR_SPECIFIC_INVENTORY"
        v-show="showApproveSpecificNetworksButton"
        class="btn btn-sm btn-success"
        name="approveCreativesForSpecificNetworks"
        :disabled="!permissions.canWriteCreativemediaownerapproval"
        @click="promptCreativesApprovalForSpecificNetworks">
        {{ `${capitalize($t('actions.approveForSpecificNetworks'))} (${selectedCreatives.length})` }}
      </button>
    </template>
    <template #expandedRow="{ creative }">
      <CreativeFilesApproval
        :title="title"
        :is-external="false"
        :creatives-table-id="creativesTableId"
        :entity-id="creative.id"
        :creative-fixed-filters="creativeFixedFilters"
        :show-creative-loader="isLoading"
        :is-from-deal-details-view="isFromDealDetailsView"
        :allow-selection="allowSelection"
        :creative-files="creative.creativeFiles" />
    </template>
    <template #link="{ creative }">
      <span :data-entity-pending-creative="creative.name">
        {{ creative.name }}
      </span>
    </template>
    <template #iabCategories="{ creative }">
      {{ creative.iabCategoriesFormatted }}
    </template>
    <template #packageCreatives="{ creative }">
      <CreativePackages :creative="creative" />
    </template>
    <template #action="{ creative }">
      <HSButton
        v-tooltip="capitalize(getActionButtonTooltip(creative))"
        :is-disabled="!canUseInventoryTargeting(creative)"
        :icon-left="markRaw(IconLocation)"
        class-name="actions-btn"
        name="approveCreative"
        variant="success"
        @click.stop="() => approveInternalCreativesForSpecificInventory([creative])">
      </HSButton>
    </template>
    <template #networkID="{ creative }">
      {{ getNetworkName(creative) }}
    </template>
    <template #buyer="{ creative }">
      {{ getValueOrDash(creative.organizationName) }}
    </template>
    <template #advertiserDescription="{ creative }">
      {{
        creative.advertiserDescription.toLowerCase().includes('unknown') ? 'UNKNOWN' : creative.advertiserDescription
      }}
    </template>
    <template #mediaOwnerApprovalDate="{ creative }">
      {{ creative.mediaOwnerApproval.approvalAtFormatted }}
    </template>
    <template #approvedRejectedBy="{ creative }">
      {{ getValueOrDash(creative.mediaOwnerApproval.approvedRejectedBy) }}
    </template>
    <template #creativeStatus="{ creative }">
      <div class="status-container">
        <ApprovalStatus :approval="creative.mediaOwnerApproval.state" />
        <div
          v-if="creative.mediaOwnerApproval.state === MediaOwnerApprovalStatus.REJECTED"
          v-tooltip="creative.mediaOwnerApproval.approvalComment"
          class="status-reject-message">
          <icon-message class="approval-message-icon" />
        </div>
      </div>
    </template>
  </CreativesApprovalCore>

  <NetworkTargetingApprovalModal
    ref="approveForSpecificNetworksModal"
    :networks="availableNetworkForApproval" />
</template>

<script setup lang="ts">
  import { CanceledError } from 'axios';
  import { capitalize, computed, markRaw, Ref, ref, useTemplateRef } from 'vue';
  import { ComponentExposed } from 'vue-component-type-helpers';
  import { useRoute } from 'vue-router';

  import TableColumn from '@/common/components/controls/table/TableColumn';
  import TableColumnTypes from '@/common/components/controls/table/TableColumnTypes';
  import HSButton from '@/common/components/ui/Button/Button.vue';
  import { IconLocation, IconMessage } from '@/common/icons/monochrome';
  import {
    Creative,
    CreativeMediaApproval,
    Filter,
    FilterCollection,
    FilterComparisonOperator,
    Network,
    QueryParams,
  } from '@/common/models';
  import CreativeFileApprovalStatus from '@/common/models/creative/CreativeFileApprovalStatus.enum';
  import InventoryTargeting from '@/common/models/InventoryTargeting';
  import MediaOwnerApprovalStatus from '@/common/models/media-owner/MediaOwnerApprovalStatus.enum';
  import ErrorHandler from '@/ErrorHandler';
  import GlobalEventBus from '@/GlobalEventBus';
  import GlobalEvents from '@/GlobalEvents';
  import { DASH, getValueOrDash } from '@/globals';
  import CreativePackages from '@/org-app/components/creatives/approval/CreativePackages.vue';
  import CreativesApprovalCore from '@/org-app/components/creatives/approval/CreativesApprovalCore.vue';
  import { ENABLE_APPROVE_FOR_SPECIFIC_INVENTORY } from '@/org-app/components/creatives/approval/EnableApproveForSpecificNetworks';
  import NetworkTargetingApprovalModal from '@/org-app/components/creatives/approval/NetworkTargetingApprovalModal.vue';
  import CreativeFilesApproval from '@/org-app/components/creatives/CreativeFilesApproval.vue';
  import ApprovalStatus from '@/org-app/components/pending-approvals/ApprovalStatus.vue';
  import { useNetworks, useUniqueAdvertisersWithBuyers } from '@/org-app/services/tanstack/queries';
  import { runQueryInScope } from '@/org-app/services/tanstack/queryUtils';
  import { mapGetters, mapOrgAppActions, mapOrgAppState } from '@/utils/StateMapping';
  import UserNotifications from '@/utils/UserNotifications';
  import i18n from '@/vueI18n';

  class CreativeInternal extends Creative {
    mediaOwnerApproval: CreativeMediaApproval;
    mediaOwnerApprovalID: number;

    static fromCreativeMediaApproval(creativeMediaApproval: CreativeMediaApproval): CreativeInternal {
      const creative = creativeMediaApproval.creative as CreativeInternal;
      creative.mediaOwnerApproval = creativeMediaApproval;
      creative.mediaOwnerApprovalID = creativeMediaApproval.id;
      return creative;
    }
  }

  const route = useRoute();

  interface IProps {
    title: string;
    isFromDealDetailsView?: boolean;
    isPastApprovals?: boolean;
  }

  const { isFromDealDetailsView, isPastApprovals } = defineProps<IProps>();

  interface IEmits {
    (event: 'approval-changed'): void;
  }

  const emits = defineEmits<IEmits>();

  const { organization, organizationSettings } = mapOrgAppState();
  const { permissions } = mapGetters();

  const networksQuery = useNetworks();

  const creativesApprovalCore = useTemplateRef<ComponentExposed<typeof CreativesApprovalCore>>('creativesApprovalCore');
  const creatives: Ref<CreativeInternal[]> = ref([]);
  const selectedCreatives: Ref<CreativeInternal[]> = ref([]);
  const isLoading = ref(true);
  const creativesTotalCount = ref(0);
  const availableNetworkForApproval: Ref<Network[]> = ref([]);
  const approveForSpecificNetworksModal = ref<InstanceType<typeof NetworkTargetingApprovalModal>>();

  const allowSelection = computed(() => !isPastApprovals);
  const showApproveForSpecificInventoryButton = computed(() => {
    if (!organizationSettings?.useInventoryTargetingForCreativeApproval) {
      return false;
    }
    // Enable the button only if all selected creatives can use inventory targeting
    return selectedCreatives.value.every((creative) => canUseInventoryTargeting(creative));
  });

  const inventoryTargetingModal = computed(() => creativesApprovalCore.value?.inventoryTargetingModal);

  const networks = computed(() => networksQuery.items.value || []);
  const creativesTableId = computed(() => {
    return `${!isPastApprovals ? 'pending' : 'past'}_approvals_creatives_list_table`;
  });

  const columns = computed(() => {
    return {
      link: TableColumn.fromObject({
        label: capitalize(i18n.global.t('names.name', { n: 1 })),
        property: `creative.name`,
        allowSort: true,
        useAsFilter: true,
      }),
      advertiserDescription: TableColumn.fromObject({
        label: capitalize(i18n.global.t('names.advertiser', { n: 1 })),
        property: `creative.advertiser.name`,
        type: TableColumnTypes.integer,
        source: () => runQueryInScope(useUniqueAdvertisersWithBuyers),
        sourceFilterProperty: `creative.advertiser.uniqueadvertiser_id`,
        useAsFilter: true,
      }),
      ...(!isFromDealDetailsView && {
        networkID: TableColumn.fromObject({
          label: capitalize(i18n.global.t('names.network', { n: 1 })),
          property: 'network_id',
          type: TableColumnTypes.integer,
          source: () => runQueryInScope(useNetworks),
        }),
      }),
      buyer: TableColumn.fromObject({
        label: capitalize(i18n.global.t('names.organization', { n: 1 })),
        property: 'organization.name',
        allowSort: false,
        useAsFilter: false,
      }),
      iabCategories: TableColumn.fromObject({
        label: capitalize(i18n.global.t('names.iabCategory', { n: 2 })),
        property: `creative.iab_categories`,
        allowSort: false,
        useAsFilter: false,
      }),
      languagesDescription: Creative.entityProperties.languages.getTableColumn({
        allowSort: false,
        useAsFilter: false,
        isEditableHandler: () => false, // Language is only editable for external creatives
      }),
      ...(!isPastApprovals && {
        estimatedStartFormatted: TableColumn.fromObject({
          label: capitalize(i18n.global.t('names.start', { n: 1 })),
          property: 'creative.flight_from',
          type: TableColumnTypes.date,
          sourceFilterProperty: 'creative.flight_from',
        }),
        estimatedEndFormatted: TableColumn.fromObject({
          label: capitalize(i18n.global.t('names.end', { n: 1 })),
          property: 'creative.flight_to',
          type: TableColumnTypes.date,
          sourceFilterProperty: 'creative.flight_to',
        }),
      }),
      ...(isPastApprovals && {
        creativeStatus: TableColumn.fromObject({
          label: capitalize(i18n.global.t('names.status', { n: 1 })),
          property: 'creative.state',
          useAsFilter: false,
        }),
        approvedRejectedBy: TableColumn.fromObject({
          label: capitalize(i18n.global.t('messages.approvedOrRejectedBy')),
          property: 'approved_rejected_by',
          allowSort: true,
        }),
        mediaOwnerApprovalDate: TableColumn.fromObject({
          label: capitalize(i18n.global.t('messages.decidedOn', { n: 1 })),
          property: 'approval_at',
          type: TableColumnTypes.date,
        }),
      }),
      packageCreatives: TableColumn.fromObject({
        label: capitalize(i18n.global.t('names.submissionSource')),
        allowSort: false,
        useAsFilter: false,
      }),
      submittedOnFormatted: TableColumn.fromObject({
        label: capitalize(i18n.global.t('messages.submittedOn')),
        property: 'submitted_at',
        type: TableColumnTypes.date,
      }),
      action: TableColumn.fromObject({
        label: capitalize(i18n.global.t('names.action', { n: 2 })),
        allowSort: false,
        useAsFilter: false,
      }),
    };
  });

  const sortColumn = computed(() =>
    isPastApprovals ? columns.value.mediaOwnerApprovalDate : columns.value.submittedOnFormatted,
  );

  const creativeFixedFilters = computed(() => [
    new FilterCollection([
      new Filter('creative.prefetch_type', [null]),
      new Filter(
        'state',
        [CreativeFileApprovalStatus.PENDING],
        !isPastApprovals ? FilterComparisonOperator.EQUAL : FilterComparisonOperator.NOT_EQUAL,
      ),
      new Filter('creative.is_fallback', [false]),
      new Filter('creative.is_mobile', [false]),
    ]),
  ]);

  const showApproveButton = computed(() =>
    selectedCreatives.value.some((creative) => creative.status !== MediaOwnerApprovalStatus.APPROVED),
  );

  const showRejectButton = computed(() =>
    selectedCreatives.value.some((creative) => creative.status !== MediaOwnerApprovalStatus.REJECTED),
  );

  const showApproveSpecificNetworksButton = computed(() => networks.value.length > 0);

  const {
    updateCreativeMediaOwnerApproval,
    getCreativeMediaOwnerApprovalTargetedNetworks,
    convertCreativeMediaOwnerApproval,
    getPackageCreatives,
  } = mapOrgAppActions();

  function canUseInventoryTargeting(creative: CreativeInternal) {
    if (!permissions.value.canWriteCreativemediaownerapproval) {
      return false; // Disabled by permissions
    }
    if (creative.mediaOwnerApproval?.state === MediaOwnerApprovalStatus.PENDING_APPROVAL) {
      return creative.mediaOwnerApproval.networkID === -1; // Creative using network approval cannot use inventory targeting
    } else {
      return creative.mediaOwnerApproval?.isApprovedWithComplexApproval; // If approved with inventory approval, inventory targeting can be used
    }
  }

  function getNetworkName(creative: CreativeInternal) {
    return creative.mediaOwnerApproval.networkID === -1
      ? i18n.global.t('messages.allNetworks')
      : networks.value.find((x) => x.id === creative.mediaOwnerApproval.networkID)?.name || DASH;
  }

  function getActionButtonTooltip(creative: CreativeInternal) {
    return !canUseInventoryTargeting(creative)
      ? i18n.global.t('messages.complexeApprovalNotAvailable')
      : i18n.global.t('actions.approveForSpecificInventory');
  }

  async function approveSelectedInternalCreatives() {
    selectedCreatives.value.forEach((creative) => {
      creative.mediaOwnerApproval.inventoryTargeting = canUseInventoryTargeting(creative)
        ? new InventoryTargeting()
        : null;
      creative.mediaOwnerApproval.state = MediaOwnerApprovalStatus.APPROVED;
    });
    await Promise.all(
      selectedCreatives.value.map((creative) =>
        updateCreativeMediaOwnerApproval({
          data: creative.mediaOwnerApproval.toApi(),
          params: {
            id: creative.mediaOwnerApproval.id,
            creativeID: creative.id,
          },
        }),
      ),
    );
    UserNotifications.success({
      title: capitalize(i18n.global.t('names.success')),
      message: i18n.global.t('messages.creativeApprovedSuccessfully', {
        n: selectedCreatives.value.length,
      }),
    });
    onApprovalChanged();
  }

  async function rejectSelectedInternalCreatives(comment?: string) {
    selectedCreatives.value.forEach((creative) => {
      creative.mediaOwnerApproval.state = MediaOwnerApprovalStatus.REJECTED;
      creative.mediaOwnerApproval.approvalComment = comment;
    });
    await Promise.all(
      selectedCreatives.value.map((creative) =>
        updateCreativeMediaOwnerApproval({
          data: creative.mediaOwnerApproval.toApi(),
          params: {
            id: creative.mediaOwnerApproval.id,
            creativeID: creative.id,
          },
        }),
      ),
    );
    UserNotifications.success({
      title: capitalize(i18n.global.t('names.success')),
      message: i18n.global.t('messages.creativeRejectedSuccessfully', {
        n: selectedCreatives.value.length,
      }),
    });
    onApprovalChanged();
  }

  async function promptCreativesApprovalForSpecificNetworks() {
    const results: number[] = await Promise.all(
      selectedCreatives.value.map((x) =>
        getCreativeMediaOwnerApprovalTargetedNetworks({ params: { id: x.mediaOwnerApproval.id } }),
      ),
    );
    availableNetworkForApproval.value = networks.value.filter((x) => results.flat().some((id) => id === x.id));

    const selectedNetworks = await approveForSpecificNetworksModal.value.selectNetworksForApproval();
    if (selectedNetworks == null || selectedNetworks.length === 0) {
      return;
    }
    //
    // Convert approvals to network level approvals.
    //
    const changes = await Promise.all(
      selectedCreatives.value
        .filter((creative) => creative.mediaOwnerApproval.networkID === -1)
        .map((creative) =>
          convertCreativeMediaOwnerApproval({
            params: { id: creative.mediaOwnerApproval.id },
            data: { approval_mode: 'NS' },
          }),
        ),
    );

    // Merge newly converted approvals to existing ones.
    const selected: CreativeInternal[] = [
      ...selectedNetworks
        .map((n) => selectedCreatives.value.filter((x) => x.mediaOwnerApproval.networkID === n.id))
        .flat(),
      ...(!changes
        ? []
        : changes
            .map((c) =>
              c.inserted != null
                ? selectedNetworks
                    .map((n) =>
                      c.inserted.filter((x) => x.network_id === n.id).map((i) => CreativeMediaApproval.fromApi(i)),
                    )
                    .flat()
                : [],
            )
            .flat()),
    ];

    selected.forEach((x) => {
      x.mediaOwnerApproval.state = MediaOwnerApprovalStatus.APPROVED;
    });

    try {
      await Promise.all(
        selected.map((creative) =>
          updateCreativeMediaOwnerApproval({
            data: creative.mediaOwnerApproval.toApi(),
            params: {
              id: creative.mediaOwnerApproval.id,
              creativeID: creative.id,
            },
          }),
        ),
      );
      UserNotifications.success({
        title: capitalize(i18n.global.t('names.success')),
        message: i18n.global.t('messages.creativeApprovedSuccessfully', {
          n: selectedCreatives.value.length,
        }),
      });
      onApprovalChanged();
    } catch (e) {
      UserNotifications.somethingWentWrong(e);
      throw e;
    }
  }

  async function approveInternalCreativesForSpecificInventory(creatives: CreativeInternal[] = selectedCreatives.value) {
    const creativeMediaApprovalIDs = creatives.map((x) => x.mediaOwnerApproval.id);
    const approved = await inventoryTargetingModal.value.approveForSpecificInventory(creativeMediaApprovalIDs);
    if (approved) {
      onApprovalChanged();
    }
  }

  async function fetchPackageCreatives() {
    const packageCreatives: CreativeInternal[] = await getPackageCreatives({ id: route.params.id });
    packageCreatives.forEach((creative) => {
      creative.mediaOwnerApproval = creative?.mediaOwnerApprovals?.find((x) => x.ownerID === organization.id);
      creative.mediaOwnerApprovalID = creative?.mediaOwnerApproval?.id;
    });
    creatives.value = packageCreatives;
  }

  async function fetchInternalCreatives(queryParams: QueryParams) {
    const { getCreativeMediaOwnerApprovals } = mapOrgAppActions();
    queryParams.expand.push(...['creative.iab_categories', 'creative.creative_languages']);
    const result = await queryParams.run(getCreativeMediaOwnerApprovals);
    const mediaOwnerApprovalCreatives = new Array<CreativeInternal>();
    for (const creativeMediaOwnerApproval of result.items) {
      if (creativeMediaOwnerApproval.ownerID === organization.id) {
        const creative = CreativeInternal.fromCreativeMediaApproval(creativeMediaOwnerApproval);
        mediaOwnerApprovalCreatives.push(creative);
      }
    }
    creatives.value = mediaOwnerApprovalCreatives;
    creativesTotalCount.value = result.totalCount;
  }

  async function fetchCreatives(queryParams: QueryParams) {
    isLoading.value = true;
    try {
      if (isFromDealDetailsView) {
        await fetchPackageCreatives();
      } else {
        await fetchInternalCreatives(queryParams);
      }
      setupCreativesMediaOwners();
      if (!isPastApprovals && !isFromDealDetailsView) {
        GlobalEventBus.$emit(GlobalEvents.pendingApprovalsChanged.creatives, creativesTotalCount.value);
      }
      isLoading.value = false;
    } catch (e) {
      if (!(e instanceof CanceledError)) isLoading.value = false;
      ErrorHandler.handleExceptionInUIComponent(e as Error);
    }
  }

  // I don't know why this is needed
  function setupCreativesMediaOwners() {
    creatives.value.forEach((creative) => {
      const mediaOwnerApprovals = creative.mediaOwnerApprovals.filter(
        (mediaOwnerApproval) => (mediaOwnerApproval.creativeID || mediaOwnerApproval.id) === creative.id,
      );
      creative.mediaOwnerApprovals = mediaOwnerApprovals;
    });
  }

  function onApprovalChanged() {
    emits('approval-changed');
    refresh();
  }

  function refresh() {
    creativesApprovalCore.value?.refresh();
  }

  defineExpose({
    refresh,
  });
</script>

<style lang="scss" rel="stylesheet/scss" scoped>
  @use '@/styles/variables' as *;

  #app {
    .creatives-table-container {
      margin-bottom: 3rem;

      .actions-btn {
        padding: 0.3rem !important;
      }

      .status-container,
      .status-reject-message {
        display: flex;
        flex-direction: row;
        align-items: center;
      }
    }
  }
</style>
